import { type Ref, computed, type ComputedRef } from "vue";
import { useRuntimeConfig } from "nuxt/app";

//
// ============================================================================
// HELPERS
// ============================================================================
//

/**
 * Check if URL is absolute (external or data URI)
 */
const isAbsoluteUrl = (url: string): boolean =>
  url.startsWith("http://") || url.startsWith("https://") || url.startsWith("//") || url.startsWith("data:");

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Simple asset path resolver for reverse proxy deployments
 *
 * Purpose:
 * - Resolves asset paths with base URL for reverse proxy deployments
 * - Supports props-based override
 * - Handles external URLs and data URIs
 *
 * Responsibilities:
 * - Prefix paths with APP_BASE_URL for reverse proxy
 * - Pass through absolute URLs unchanged
 * - Support reactive prop-based customization
 *
 * @param defaultPath - Default asset path (e.g., "/logo.png", "/img/logo.png")
 * @param customPath - Optional reactive custom path (from props)
 * @returns Computed asset path with base URL prefix
 */
export function useAssetPath(defaultPath: string, customPath?: Ref<string | null | undefined>): ComputedRef<string> {
  const config = useRuntimeConfig();
  const baseUrl = (config.public.APP_BASE_URL as string) || "/";

  return computed(() => {
    // Use custom path if provided, otherwise use default
    const path = customPath?.value?.trim() || defaultPath.trim();

    // Pass through absolute URLs unchanged (http://, https://, //, data:)
    if (isAbsoluteUrl(path)) {
      return path;
    }

    // Combine with base URL for relative paths
    return pathCombine(baseUrl, path);
  });
}
